/**
 * SolutionContext — Shares active solution work with the chat agent
 * and tracks background AI tasks for the solution list.
 *
 * When a user closes the modal while AI is working, the task is
 * registered here so the solution list can show a glimmering progress bar.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const SolutionContext = createContext(null);

export function SolutionProvider({ children }) {
  const [activeSolution, setActiveSolution] = useState(null);

  // Background AI tasks: { [solutionId]: { type, message, promise } }
  const [backgroundTasks, setBackgroundTasks] = useState({});

  const setSolutionContext = useCallback((ctx) => {
    setActiveSolution(ctx);
  }, []);

  const clearSolutionContext = useCallback(() => {
    setActiveSolution(null);
  }, []);

  // Register a background AI task (enrichment or analysis)
  const addBackgroundTask = useCallback((solutionId, { type, message, promise, onComplete, onError }) => {
    setBackgroundTasks(prev => ({ ...prev, [solutionId]: { type, message } }));
    // When promise resolves, remove the task
    if (promise) {
      promise
        .then(() => {
          onComplete?.();
          setBackgroundTasks(prev => {
            const next = { ...prev };
            delete next[solutionId];
            return next;
          });
        })
        .catch(() => {
          onError?.();
          setBackgroundTasks(prev => {
            const next = { ...prev };
            delete next[solutionId];
            return next;
          });
        });
    }
  }, []);

  const removeBackgroundTask = useCallback((solutionId) => {
    setBackgroundTasks(prev => {
      const next = { ...prev };
      delete next[solutionId];
      return next;
    });
  }, []);

  const getBackgroundTask = useCallback((solutionId) => {
    return backgroundTasks[solutionId] || null;
  }, [backgroundTasks]);

  return (
    <SolutionContext.Provider value={{
      activeSolution, setSolutionContext, clearSolutionContext,
      backgroundTasks, addBackgroundTask, removeBackgroundTask, getBackgroundTask,
    }}>
      {children}
    </SolutionContext.Provider>
  );
}

export function useSolutionContext() {
  return useContext(SolutionContext) || {
    activeSolution: null,
    setSolutionContext: () => {},
    clearSolutionContext: () => {},
    backgroundTasks: {},
    addBackgroundTask: () => {},
    removeBackgroundTask: () => {},
    getBackgroundTask: () => null,
  };
}
